import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Header,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Label,
  Textarea,
  Empty,
  EmptyTitle,
  EmptyDescription,
  Spinner,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { extractAttributes } from '@/utils/api-client';
import {
  guidedUseCaseService,
  scopeKeyFor,
  type ElementStandard,
  type LinkedResource,
  type StandardWriteData,
} from '@/services/guided-use-case-service';
import { elementStatus } from './status';
import { ListField } from './list-field';
import { RelatedResourcePicker } from './related-resource-picker';

interface EditorForm {
  definition_of_good: string;
  dos: string[];
  donts: string[];
  worked_example: string;
  completion_criteria: string;
  resources: LinkedResource[];
}

const EMPTY_FORM: EditorForm = {
  definition_of_good: '',
  dos: [],
  donts: [],
  worked_example: '',
  completion_criteria: '',
  resources: [],
};

function formFrom(standard: ElementStandard | null): EditorForm {
  if (!standard) return EMPTY_FORM;

  return {
    definition_of_good: standard.definition_of_good ?? '',
    dos: standard.dos ?? [],
    donts: standard.donts ?? [],
    worked_example: standard.worked_example ?? '',
    completion_criteria: standard.completion_criteria ?? '',
    resources: standard.resources ?? [],
  };
}

function writeDataFrom(form: EditorForm): StandardWriteData {
  return {
    definition_of_good: form.definition_of_good.trim() || null,
    dos: form.dos.map((item) => item.trim()).filter(Boolean),
    donts: form.donts.map((item) => item.trim()).filter(Boolean),
    worked_example: form.worked_example.trim() || null,
    completion_criteria: form.completion_criteria.trim() || null,
    resources: form.resources,
  };
}

export function KaiyaElementStandardEditorPage() {
  const { canvasKey, slot } = useParams<{ canvasKey: string; slot: string }>();
  const scopeKey = canvasKey && slot ? scopeKeyFor(canvasKey, slot) : '';
  const { setShowPageHeader } = usePageContext();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<EditorForm>(EMPTY_FORM);
  const [showPicker, setShowPicker] = useState(false);
  const [showListErrors, setShowListErrors] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data: canvasData } = useQuery({
    queryKey: ['guidedCanvas', canvasKey],
    queryFn: () => guidedUseCaseService.getCanvas(canvasKey!),
    enabled: !!canvasKey,
  });

  const canvasTitle = canvasData?.data.title ?? canvasKey;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['guidedElement', scopeKey],
    queryFn: () => guidedUseCaseService.getElement(scopeKey),
    enabled: !!scopeKey,
  });

  const element = data?.element;
  const standard = extractAttributes<ElementStandard>(data?.guidance?.data);

  useEffect(() => {
    if (data && loadedFor !== scopeKey) {
      setForm(formFrom(standard));
      setLoadedFor(scopeKey);
    }
  }, [data, standard, scopeKey, loadedFor]);

  const writeData = useMemo(() => writeDataFrom(form), [form]);
  const debounced = useDebounced(writeData, 400);

  const savedData = useMemo(() => JSON.stringify(writeDataFrom(formFrom(standard))), [standard]);
  const isDirty = loadedFor === scopeKey && JSON.stringify(debounced) !== savedData;

  const { data: previewData } = useQuery({
    queryKey: ['guidedElementPreview', scopeKey, JSON.stringify(debounced)],
    queryFn: () => guidedUseCaseService.previewPrompt(scopeKey, debounced),
    enabled: !!scopeKey && isDirty,
    placeholderData: keepPreviousData,
  });

  const preview = isDirty ? previewData?.data ?? data?.preview : data?.preview;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (standard) return guidedUseCaseService.updateStandard(standard.id, writeData);
      return guidedUseCaseService.createStandard({ ...writeData, scope_key: scopeKey });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guidedElement', scopeKey] });
      queryClient.invalidateQueries({ queryKey: ['guidedCanvas', canvasKey] });
      queryClient.invalidateQueries({ queryKey: ['guidedUseCases'] });
      toast.success('Definition saved');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to save'),
  });

  const missingFields = [
    ['a definition of good', writeData.definition_of_good],
    ['a worked example', writeData.worked_example],
    ['completion criteria', writeData.completion_criteria],
  ].filter(([, value]) => !value).map(([label]) => label as string);

  const hasBlankListEntry =
    form.dos.some((item) => !item.trim()) || form.donts.some((item) => !item.trim());

  const resourcesOverCeiling = preview?.resources_within_ceiling === false;

  const canSave = missingFields.length === 0 && !resourcesOverCeiling;

  const handleSave = () => {
    if (hasBlankListEntry) {
      setShowListErrors(true);
      toast.error('Fill in or remove the empty do / don\u2019t');
      return;
    }

    setShowListErrors(false);
    saveMutation.mutate();
  };

  const title = element?.title ?? slot ?? '';
  const status = element ? elementStatus(element) : null;
  const busy = saveMutation.isPending;

  return (
    <>
      <Header
        title={title}
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/kaiya-guided-use-cases">Guided Use Cases</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/kaiya-guided-use-cases/${canvasKey}`}>{canvasTitle}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      >
        {status && (
          <Badge variant={status.variant} tint className="bg-white">
            {status.label}
          </Badge>
        )}
      </Header>

      <ContentContainer>
        {isLoading ? (
          <Empty><Spinner /></Empty>
        ) : isError || !element ? (
          <Empty>
            <EmptyTitle>Element not found</EmptyTitle>
            <EmptyDescription>
              The registry has no element "{slot}" on canvas "{canvasKey}".
            </EmptyDescription>
          </Empty>
        ) : (
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-3">
            <Card className="min-w-0 md:col-span-2">
              <CardContent className="flex flex-col gap-6 pt-6">
                <div>
                  <Label className="text-xs font-semibold">Definition of good</Label>
                  <Textarea
                    className="mt-2 min-h-[140px]"
                    value={form.definition_of_good}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, definition_of_good: e.target.value }))
                    }
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <ListField
                    label="Do"
                    tone="do"
                    showErrors={showListErrors}
                    values={form.dos}
                    onChange={(dos) => setForm((prev) => ({ ...prev, dos }))}
                  />
                  <ListField
                    label="Don't"
                    tone="dont"
                    showErrors={showListErrors}
                    values={form.donts}
                    onChange={(donts) => setForm((prev) => ({ ...prev, donts }))}
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold">Worked example</Label>
                  <Textarea
                    className="mt-2"
                    value={form.worked_example}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, worked_example: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold">Completion criteria</Label>
                  <Textarea
                    className="mt-2"
                    value={form.completion_criteria}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, completion_criteria: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold">Related resources</Label>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {form.resources.map((resource) => (
                      <span
                        key={resource.contentful_entry_id}
                        className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs"
                      >
                        {resource.title}
                        <button
                          type="button"
                          aria-label={`Remove ${resource.title}`}
                          className="cursor-pointer text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              resources: prev.resources.filter(
                                (item) => item.contentful_entry_id !== resource.contentful_entry_id
                              ),
                            }))
                          }
                        >
                          &times;
                        </button>
                      </span>
                    ))}

                    <Button variant="outline" size="sm" onClick={() => setShowPicker(true)}>
                      + Add related resource
                    </Button>
                  </div>

                  {resourcesOverCeiling && (
                    <p className="mt-2 text-xs text-destructive">
                      Too much to send — remove one, or shorten its AI Content in Contentful.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-4">
                  {!canSave && (
                    <span className="text-xs text-muted-foreground">
                      {missingFields.length > 0
                        ? `Still needs ${missingFields.join(', ')}`
                        : 'Too much related resource content to send'}
                    </span>
                  )}
                  <Button
                    disabled={busy || !canSave}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="min-w-0 gap-4 py-5 md:sticky md:top-6 md:py-5">
              <CardHeader className="px-4 md:px-4">
                <CardTitle className="text-base">Preview injected prompt</CardTitle>
              </CardHeader>
              <CardContent className="p-0 md:p-0">
                <div className="px-4">
                  <pre className="whitespace-pre-wrap break-words rounded-md bg-muted px-3 py-2.5 font-mono text-[11.5px] leading-relaxed text-neutral-700">
                    {preview?.authored
                      ? preview.block
                      : 'Nothing is injected yet — this element has no definition of good.'}
                  </pre>
                </div>

                {preview?.omitted_sections && preview.omitted_sections.length > 0 && (
                  <div className="mt-4 border-t px-4 pt-3 text-xs text-warning-700">
                    Dropped to fit the budget: {preview.omitted_sections.join(', ')}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </ContentContainer>

      <RelatedResourcePicker
        open={showPicker}
        onOpenChange={setShowPicker}
        linked={form.resources}
        onAdd={(resource) =>
          setForm((prev) => ({ ...prev, resources: [...prev.resources, resource] }))
        }
      />
    </>
  );
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}
